/**
 * Collections over MCP — the UPDATE half (PLAN §H tranche 3).
 *
 * `request_update`, `request_move`, `collection_update`, `folder_rename`.
 * Beside `collectionWrites.js` (creates) and `collectionDeletes.js`
 * (deletion), because the three fail differently and share nothing but the one
 * write path.
 *
 * ## The three rules tranche 1 established, unchanged, and why they still bind
 *
 * 1. **Everything goes through `applyCollectionsChange`**, which routes the
 *    write through `setCollections`. A write that reached storage any other way
 *    is REVERTED by the user's next keystroke — `collectionsDb.js`'s header is
 *    the argument and it is the most important comment in this feature.
 * 2. **`next` is DERIVED from the array handed in by a single-record change.**
 *    The persist that follows tombstones every collection and request absent
 *    from that array, on every device, with no undo. `assertNoCollectionLost`
 *    refuses the write if a change function ever gets this wrong, and an update
 *    declares NO allowed removals at all — it may remove nothing.
 * 3. **Records are patched, never rebuilt.** `applyRequestPatch` carries every
 *    field the client did not name straight through, including the ones this
 *    server may not read: `preRequestScript`, `testScript` (**O33**) and
 *    `mock`. Rebuilding from `buildRequest` would blank a script the user
 *    wrote — a silent destructive edit by a tool that promised not to touch
 *    scripts.
 *
 * ## The two rules tranche 3 adds
 *
 * 4. **`expected_rev` on anything that replaces text a human may have typed.**
 *    Read `mcp/collectionRev.js` before trusting it: on this surface there is
 *    no `rev` column and no CAS, so the rev is a content hash and the check is
 *    a JS comparison inside the module mutex. It detects the user's edit
 *    between the agent's read and the agent's write, which is what it is for;
 *    it is not atomic against a keystroke landing in the same React commit,
 *    and nothing here pretends otherwise.
 *
 * 5. **The OPEN EDITOR is patched (PLAN §F2).** A request tab holds a full
 *    denormalized copy of the record and writes it back whole on Ctrl+S, so an
 *    update the tab does not know about is reverted the moment the user saves.
 *    Every change here that alters a request declares a `tabPatches` entry, and
 *    `applyCollectionsChange` applies it after the store write — in the one
 *    write path, so a handler cannot forget it. See `mcp/requestTabSync.js`.
 *
 * ## What these tools still cannot do
 *
 * Structural, not pending: no response, no request history, no variable value
 * (**O7**), no script as readable or writable text (**O33**), no mock server
 * (**O31**), no workspace but the open one (**O27**), no cross-collection move
 * (a data-residency change, out of scope), and no literal credential written
 * into an auth block or a url (**O32** — checked here on the way in exactly as
 * it is on a create).
 */
import { mcpError } from '../errors';
import {
  applyRequestPatch,
  checkAuth,
  checkBody,
  checkKvRows,
  checkMethod,
  checkText,
  checkUrl,
  collectionNotFound,
  nextSortOrder,
  pathOf,
} from '../collectionRecords';
import {
  applyCollectionsChange,
  findCollection,
  findFolder,
  findRequest,
  requireRev,
} from '../collectionsDb';
import { revOfCollection, revOfRequest } from '../collectionRev';
import { tabPatchFromRequest } from '../requestTabSync';
import { requestKind, UNSUPPORTED_FIELDS_BY_KIND } from '../protocolProjection';
import {
  getFolderById,
  moveRequestToFolder,
  normalizeParentId,
  renameFolderInCollection,
} from '../../utils/collectionFolders';
import { defaultCollectionAuth } from '../../utils/requestAuth';

/** `not_found` for a request, phrased so the model lists rather than retries. */
function requestNotFound(requestId) {
  return mcpError(
    'not_found',
    'No request with that id in the open workspace. Nothing was written. Call collections_list with include_requests and use an id it returns.',
    { requestId: String(requestId ?? '') }
  );
}

/** Replace one request inside one collection, in the whole array. Rule 2. */
function withRequest(collections, collectionId, nextRequest) {
  return collections.map((c) =>
    String(c.id) === String(collectionId)
      ? {
          ...c,
          requests: (c.requests || []).map((r) =>
            String(r.id) === String(nextRequest.id) ? nextRequest : r
          ),
        }
      : c
  );
}

/** Replace one collection in the whole array. Rule 2. */
function withCollection(collections, nextCollection) {
  return collections.map((c) =>
    String(c.id) === String(nextCollection.id) ? nextCollection : c
  );
}

const KIND_LABEL = Object.freeze({ grpc: 'gRPC', websocket: 'WebSocket', sse: 'SSE' });

/** `unsupported_for_type` when the patch names a field this request kind ignores. */
function unsupportedFieldsFor(request, changed) {
  const kind = requestKind(request);
  const refused = changed.filter((field) => UNSUPPORTED_FIELDS_BY_KIND[kind].includes(field));
  if (!refused.length) return null;
  return mcpError(
    'unsupported_for_type',
    `This is a ${KIND_LABEL[kind]} request, and it does not use ${refused.join(', ')} — Apiboo would store the change and never send it. Nothing was written. request_read shows the fields it does use in \`protocol\`; those cannot be changed over MCP yet, so ask the user to edit them in Apiboo.`,
    { requestType: kind, fields: refused }
  );
}

export async function requestUpdate(args = {}) {
  // Argument checks first and in a fixed order, so a call with two problems
  // always reports the same one — a refusal that moves around is a refusal a
  // model cannot learn from. Only the fields the client SENT are checked; a
  // stored record that already violates a rule is not this call's business.
  const has = (key) => Object.prototype.hasOwnProperty.call(args, key) && args[key] != null;
  const refusal =
    (has('method') ? checkMethod(args.method) : null) ||
    (has('url') ? checkUrl(args.url) : null) ||
    (has('name') ? checkText(args.name, 'name', 300) : null) ||
    (has('description') ? checkText(args.description, 'description') : null) ||
    (has('headers') ? checkKvRows(args.headers, 'headers') : null) ||
    (has('params') ? checkKvRows(args.params, 'params') : null) ||
    (has('body') ? checkBody(args.body) : null) ||
    (has('auth') ? checkAuth(args.auth) : null);
  if (refusal) return refusal;

  const changed = ['name', 'method', 'url', 'description', 'headers', 'params', 'body', 'auth']
    .filter(has);
  if (!changed.length) {
    return mcpError(
      'invalid_argument',
      'request_update was called with nothing to change. Send at least one of name, method, url, description, headers, params, body or auth.'
    );
  }

  return applyCollectionsChange((collections) => {
    const found = findRequest(collections, args.request_id);
    if (!found) return { error: requestNotFound(args.request_id) };

    // A gRPC / WebSocket / SSE request ignores some HTTP fields; writing one
    // would be a silent no-op the user only finds on send (card 5258c71d).
    // Before the rev check: no rev makes these fields apply.
    const unsupported = unsupportedFieldsFor(found.request, changed);
    if (unsupported) return { error: unsupported };

    const stale = requireRev(args.expected_rev, revOfRequest(found.request), 'that request');
    if (stale) return { error: stale };

    const nextRequest = applyRequestPatch(found.request, args);
    return {
      next: withRequest(collections, found.collection.id, nextRequest),
      sizedRecords: [nextRequest],
      // **PLAN §F2.** The tab, if one is open, and the revision bump with it.
      tabPatches: [{ requestId: nextRequest.id, patch: tabPatchFromRequest(nextRequest) }],
      result: {
        updated: 'request',
        collectionId: String(found.collection.id),
        requestId: String(nextRequest.id),
        name: String(nextRequest.name || ''),
        method: String(nextRequest.method || 'GET'),
        // The rev to pass to the NEXT update. Returning it turns a sequence of
        // edits into one read plus N writes rather than N reads and N writes.
        rev: revOfRequest(nextRequest),
        changedFields: changed,
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
}

/**
 * Move a request to another folder of the SAME collection, or to its root.
 *
 * **`expected_rev` is OPTIONAL here and that is a decision, not an omission.**
 * PLAN §D1.4 requires it on every tool "that replaces text a human may have
 * typed" and a move replaces none — PLAN §D2's own argument table lists no
 * `expected_rev` for this tool for that reason. Requiring one would force a
 * `request_read` before every tidy-up of an imported spec, which is the exact
 * workflow this tranche exists to enable. When a caller DOES pass one it is
 * checked in full, so a client that wants compare-and-swap on a move has it.
 *
 * A CROSS-COLLECTION move is refused rather than supported: it is a
 * data-residency change (the `moveBoardToTeam` precedent) and it is out of
 * scope for this phase.
 */
export async function requestMove(args = {}) {
  if (args.position != null && (!Number.isInteger(args.position) || args.position < 0)) {
    return mcpError('invalid_argument', '`position` must be a whole number, 0 or greater.');
  }

  return applyCollectionsChange((collections) => {
    const found = findRequest(collections, args.request_id);
    if (!found) return { error: requestNotFound(args.request_id) };

    if (args.expected_rev != null && args.expected_rev !== '') {
      const stale = requireRev(args.expected_rev, revOfRequest(found.request), 'that request');
      if (stale) return { error: stale };
    }

    const folderId = normalizeParentId(args.folder_id);
    if (folderId && !getFolderById(found.collection, folderId)) {
      return {
        error: mcpError(
          'invalid_parent',
          'That folder is not in this request\'s collection. Nothing was moved. A request can only move between folders of the SAME collection; moving one to another collection is not available here.',
          { collectionId: String(found.collection.id), folderId }
        ),
      };
    }

    // The app's own helper, which also strips the stale `folder_id` out of the
    // record's `settings` payload — a detail a re-typed move would lose.
    const movedCollection = moveRequestToFolder(found.collection, found.request.id, folderId);
    const moved = (movedCollection.requests || []).find(
      (r) => String(r.id) === String(found.request.id)
    );
    if (!moved) {
      return { error: mcpError('write_failed', 'That request could not be moved. Nothing was written.') };
    }

    // Placement inside the target: `position` is an index among that folder's
    // existing children, and no argument means "at the end", which is where
    // the app puts a newly created sibling.
    const sortOrder =
      args.position == null
        ? nextSortOrder({ ...movedCollection, requests: (movedCollection.requests || []).filter((r) => String(r.id) !== String(moved.id)) }, folderId)
        : args.position;
    const placed = { ...moved, sort_order: sortOrder };
    const nextCollection = {
      ...movedCollection,
      requests: (movedCollection.requests || []).map((r) =>
        String(r.id) === String(placed.id) ? placed : r
      ),
    };

    return {
      next: withCollection(collections, nextCollection),
      sizedRecords: [placed],
      tabPatches: [{ requestId: placed.id, patch: tabPatchFromRequest(placed) }],
      result: {
        updated: 'request',
        moved: true,
        collectionId: String(nextCollection.id),
        requestId: String(placed.id),
        name: String(placed.name || ''),
        folderId,
        path: pathOf(nextCollection, folderId),
        rev: revOfRequest(placed),
        note: 'A collection or folder in the sidebar may be collapsed, so this may not be visible until the user expands it — tell them the collection and folder by name.',
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
}

export async function collectionUpdate(args = {}) {
  const has = (key) => Object.prototype.hasOwnProperty.call(args, key) && args[key] != null;
  const refusal =
    (has('name') ? checkText(args.name, 'name', 200) : null) ||
    (has('description') ? checkText(args.description, 'description') : null) ||
    (has('auth') ? checkAuth(args.auth) : null);
  if (refusal) return refusal;

  const changed = ['name', 'description', 'auth'].filter(has);
  if (!changed.length) {
    return mcpError(
      'invalid_argument',
      'collection_update was called with nothing to change. Send at least one of name, description or auth.'
    );
  }
  if (has('name') && !String(args.name).trim()) {
    return mcpError('invalid_argument', '`name` must not be empty.');
  }

  return applyCollectionsChange((collections) => {
    const collection = findCollection(collections, args.collection_id);
    if (!collection) return { error: collectionNotFound(args.collection_id) };

    const stale = requireRev(args.expected_rev, revOfCollection(collection), 'that collection');
    if (stale) return { error: stale };

    const nextCollection = { ...collection };
    if (has('name')) nextCollection.name = String(args.name).trim();
    if (has('description')) nextCollection.description = String(args.description);
    if (has('auth')) {
      // The COLLECTION-level auth block, which every request with
      // `auth.type: "inherit"` resolves to. It went through `checkAuth`
      // above, so **O32** covers it: a literal credential here would be
      // inherited by every request in the collection at once.
      const base = collection.settings?.collection_auth || defaultCollectionAuth();
      nextCollection.settings = {
        ...(collection.settings || {}),
        collection_auth: {
          ...base,
          type: args.auth.type ? String(args.auth.type) : base.type,
          bearer: typeof args.auth.bearer === 'string' ? args.auth.bearer : base.bearer || '',
          apikey: { ...(base.apikey || {}), ...(args.auth.apikey || {}) },
          basic: { ...(base.basic || {}), ...(args.auth.basic || {}) },
        },
      };
    }

    return {
      next: withCollection(collections, nextCollection),
      sizedRecords: [{ ...nextCollection, requests: undefined }],
      result: {
        updated: 'collection',
        collectionId: String(nextCollection.id),
        name: String(nextCollection.name || ''),
        description: typeof nextCollection.description === 'string' ? nextCollection.description : '',
        rev: revOfCollection(nextCollection),
        changedFields: changed,
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
}

/**
 * Rename a folder. `expected_rev` is the COLLECTION's rev, not a folder's —
 * folders are not rows. They live inside the collection's own `data` JSON at
 * `settings.folders[]` (`sync/store.rs:307-327` says so in its own comment), so
 * the collection record is the unit of change and its rev is the only honest
 * thing to compare.
 */
export async function folderRename(args = {}) {
  const name = typeof args.name === 'string' ? args.name.trim() : '';
  if (!name) return mcpError('invalid_argument', '`name` is required and must not be empty.');
  const badText = checkText(args.name, 'name', 200);
  if (badText) return badText;

  return applyCollectionsChange((collections) => {
    const collection = findCollection(collections, args.collection_id);
    if (!collection) return { error: collectionNotFound(args.collection_id) };

    const folder = findFolder(collection, args.folder_id);
    if (!folder) {
      return {
        error: mcpError(
          'not_found',
          'No folder with that id in this collection. Nothing was written. Call collection_read and use a folder id it returns.',
          { collectionId: String(collection.id), folderId: String(args.folder_id ?? '') }
        ),
      };
    }

    const stale = requireRev(args.expected_rev, revOfCollection(collection), 'that collection');
    if (stale) return { error: stale };

    const nextCollection = renameFolderInCollection(collection, folder.id, name);
    return {
      next: withCollection(collections, nextCollection),
      sizedRecords: [{ ...nextCollection, requests: undefined }],
      result: {
        updated: 'folder',
        collectionId: String(nextCollection.id),
        folderId: String(folder.id),
        name,
        path: pathOf(nextCollection, folder.id),
        // The COLLECTION's new rev — the thing a following `folder_rename` or
        // `collection_update` has to pass back.
        rev: revOfCollection(nextCollection),
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
}
